import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell,
  TableBody, TablePagination, CircularProgress, Alert, Chip,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { API_BASE_URL } from "../../config";

const ACTION_COLOR = {
  CREATE: { bg: "#e8f5e9", color: "#2e7d32" },
  UPDATE: { bg: "#e3f2fd", color: "#1565c0" },
  DELETE: { bg: "#fdecea", color: "#c62828" },
  SUSPEND: { bg: "#fff3e0", color: "#e65100" },
  ACTIVATE: { bg: "#e8f5e9", color: "#2e7d32" },
};

function ActionChip({ action }) {
  const style = ACTION_COLOR[action?.toUpperCase()] || { bg: "#f5f5f5", color: "#616161" };
  return (
    <Chip
      label={action}
      size="small"
      sx={{ bgcolor: style.bg, color: style.color, fontWeight: 600, fontSize: "11px" }}
    />
  );
}

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/superadmin/audit`);
      setRows(res.data.logs || res.data.log || []);
    } catch {
      setError("Failed to load audit log.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let data = [...rows];
    if (actionFilter !== "all") data = data.filter((r) => r.action?.toLowerCase() === actionFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.actor?.toLowerCase().includes(q) ||
          r.entity_name?.toLowerCase().includes(q) ||
          r.entity_type?.toLowerCase().includes(q) ||
          r.action?.toLowerCase().includes(q)
      );
    }
    setFiltered(data);
    setPage(0);
  }, [rows, search, actionFilter]);

  const actions = ["all", ...Array.from(new Set(rows.map((r) => r.action?.toLowerCase()).filter(Boolean)))];
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Audit Log</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        All super-admin actions recorded for compliance
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search actor, entity…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 280, bgcolor: "#fff" }}
        />
        <FormControl size="small" sx={{ minWidth: 150, bgcolor: "#fff" }}>
          <InputLabel>Action</InputLabel>
          <Select
            value={actionFilter}
            label="Action"
            onChange={(e) => setActionFilter(e.target.value)}
          >
            {actions.map((a) => (
              <MenuItem key={a} value={a} sx={{ textTransform: "capitalize" }}>{a}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              {["Time", "Action", "Entity Type", "Entity", "Actor", "Details"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 600, fontSize: "12px", color: "#667085" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No audit records found.
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell><ActionChip action={r.action} /></TableCell>
                  <TableCell sx={{ fontSize: "12px", textTransform: "capitalize" }}>{r.entity_type || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "12px" }}>{r.entity_name || r.entity_id || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "12px" }}>{r.actor || "—"}</TableCell>
                  <TableCell sx={{ fontSize: "12px", maxWidth: 260 }}>
                    {r.details && Object.keys(r.details).length > 0
                      ? Object.entries(r.details)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(", ")
                          .slice(0, 80)
                      : "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>
    </Box>
  );
}
